import type {
  AccountingReportDocumentInput,
  CustomerStatementDocumentInput,
  DocumentDataInput,
  DocumentEntityRefInput,
  DocumentReportCompareInput,
  DocumentReportPeriodInput,
  DocumentTemplateEntityType,
} from 'types/graphql';

/**
 * D1 `documentData(input)` mapping, shared by the document preview (`useDocumentData`) and the expenses refetch
 * predicate. It lives in `lib`, not behind the `entities/documents` barrel, so the expenses import graph does not
 * pull the whole preview/generator tree.
 */

/** The `DocumentDataInput` arm (D1, `@oneOf`) for each document entity type. */
const ARM_OF = {
  SALES_ORDER: 'salesOrder',
  PURCHASE_ORDER: 'purchaseOrder',
  GROWER_PRODUCT: 'growerProduct',
  BUY_SELL_ORDER: 'buySellOrder',
  PRODUCTION_RUN: 'productionRun',
  INVOICE: 'invoice',
  REMITTANCE: 'remittance',
  CUSTOMER_STATEMENT: 'customerStatement',
  INVENTORY_TRANSFER: 'inventoryTransfer',
  GROWER_SETTLEMENT: 'growerSettlement',
  BALANCE_SHEET_REPORT: 'balanceSheetReport',
  PROFIT_AND_LOSS_REPORT: 'profitAndLossReport',
} as const satisfies Record<DocumentTemplateEntityType, keyof DocumentDataInput>;

const ENTITY_TYPES = Object.keys(ARM_OF).filter((value): value is DocumentTemplateEntityType => value in ARM_OF);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isDocumentTemplateEntityType = (value: unknown): value is DocumentTemplateEntityType =>
  typeof value === 'string' && value in ARM_OF;

/** The record getters read only `id` and `slug` (`fetchDocumentData.ts`). */
const entityRef = ({ id, slug }: Record<string, unknown>): DocumentEntityRefInput => ({
  ...(typeof id === 'number' && Number.isInteger(id) ? { id } : {}),
  ...(typeof slug === 'string' ? { slug } : {}),
});

/** Aging Reports sends Cube's string id; the editor's variable field sends a parsed number. */
const statementRef = ({ businessEntityId }: Record<string, unknown>): CustomerStatementDocumentInput =>
  typeof businessEntityId === 'string' || (typeof businessEntityId === 'number' && Number.isFinite(businessEntityId))
    ? { businessEntityId: String(businessEntityId) }
    : {};

const reportPeriodOf = (value: unknown): DocumentReportPeriodInput | undefined => {
  if (!isRecord(value)) return undefined;
  const { start, end } = value;
  return { ...(typeof start === 'string' ? { start } : {}), ...(typeof end === 'string' ? { end } : {}) };
};

const compareToOf = (value: unknown): DocumentReportCompareInput | undefined => {
  if (!isRecord(value)) return undefined;
  const { previousPeriod, previousYear } = value;
  return {
    ...(typeof previousPeriod === 'boolean' ? { previousPeriod } : {}),
    ...(typeof previousYear === 'boolean' ? { previousYear } : {}),
  };
};

/**
 * The report getters read `reportPeriod`, `compareTo` and `hideZeroBalance` only; the account rows the
 * report pages pass along are never read, so they are not sent.
 */
const reportRef = (variables: Record<string, unknown>): AccountingReportDocumentInput => {
  const reportPeriod = reportPeriodOf(variables.reportPeriod);
  const compareTo = compareToOf(variables.compareTo);
  const { hideZeroBalance } = variables;
  return {
    ...(reportPeriod ? { reportPeriod } : {}),
    ...(compareTo ? { compareTo } : {}),
    ...(typeof hideZeroBalance === 'boolean' ? { hideZeroBalance } : {}),
  };
};

/** D1: the preview's `(entityType, variables)` → the typed `documentData(input)` arm, with the same data. */
export function toDocumentDataInput(
  entityType: DocumentTemplateEntityType,
  variables: Record<string, unknown>
): DocumentDataInput {
  switch (entityType) {
    case 'SALES_ORDER':
      return { salesOrder: entityRef(variables) };
    case 'PURCHASE_ORDER':
      return { purchaseOrder: entityRef(variables) };
    case 'GROWER_PRODUCT':
      return { growerProduct: entityRef(variables) };
    case 'BUY_SELL_ORDER':
      return { buySellOrder: entityRef(variables) };
    case 'PRODUCTION_RUN':
      return { productionRun: entityRef(variables) };
    case 'INVOICE':
      return { invoice: entityRef(variables) };
    case 'REMITTANCE':
      return { remittance: entityRef(variables) };
    case 'INVENTORY_TRANSFER':
      return { inventoryTransfer: entityRef(variables) };
    case 'GROWER_SETTLEMENT':
      return { growerSettlement: entityRef(variables) };
    case 'CUSTOMER_STATEMENT':
      return { customerStatement: statementRef(variables) };
    case 'BALANCE_SHEET_REPORT':
      return { balanceSheetReport: reportRef(variables) };
    case 'PROFIT_AND_LOSS_REPORT':
      return { profitAndLossReport: reportRef(variables) };
  }
}

/** The entity type an executed `documentData(input)` names, or `undefined` when no single arm is set. */
export function entityTypeOfInput(input: unknown): DocumentTemplateEntityType | undefined {
  if (!isRecord(input)) return undefined;
  const named = ENTITY_TYPES.filter((entityType) => isRecord(input[ARM_OF[entityType]]));
  return named.length === 1 ? named[0] : undefined;
}
